import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CheckCircle2, Clock, Globe2, Info, MapPinned, PackageSearch, PauseCircle, RotateCcw, Store, Timer, Truck } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Card, cx, EmptyState, Skeleton, Tip } from "@/components/ui";
import { api, type TrackingOverview } from "@/lib/api";
import { countryName, fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

type Days = 7 | 30 | 90;
const pct = (x: number | null | undefined) => (x == null ? "—" : `${(x * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} %`);
const dur = (h: number | null | undefined) => (h == null ? "—" : h < 48 ? `${h.toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} h` : `${(h / 24).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} Tage`);

/** Leitzonen der deutschen PLZ (erste Ziffer) – grobe Orientierung */
const ZONES: Record<string, string> = {
  "0": "Sachsen, Thüringen-Ost",
  "1": "Berlin, Brandenburg, MV",
  "2": "Hamburg, Bremen, SH, Nord-NDS",
  "3": "Niedersachsen, Nordhessen",
  "4": "Ruhrgebiet, Münsterland",
  "5": "Rheinland, Westerwald",
  "6": "Hessen, Saarland, Pfalz",
  "7": "Baden-Württemberg",
  "8": "Südbayern",
  "9": "Nordbayern, Thüringen",
};

const REASON: Record<TrackingOverview["attention"][number]["reason"], { label: string; tone: "danger" | "warning" | "neutral" }> = {
  problem: { label: msg("Zustellproblem"), tone: "danger" },
  returned: { label: msg("Zurück an Absender"), tone: "danger" },
  pickup_ready: { label: msg("Liegt zur Abholung"), tone: "warning" },
  stuck: { label: msg("Hängt fest"), tone: "warning" },
  no_data: { label: msg("Keine Daten von DHL"), tone: "neutral" },
};

/** Track & Trace: wo sind die Pakete gerade, wie schnell kommen sie an, wo hakt es? */
export function TrackingPage() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const days = ([7, 30, 90] as const).find((d) => d === Number(params.get("days"))) ?? 30;
  const q = useQuery({ queryKey: ["tracking-overview", tenantId, days], queryFn: () => api.trackingOverview(tenantId, days), placeholderData: keepPreviousData, refetchInterval: 5 * 60_000 });
  const d = q.data;
  const setDays = (x: Days) => setParams(x === 30 ? {} : { days: String(x) }, { replace: true });

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Track & Trace")}
        description={t("Wo eure Pakete gerade sind, wie schnell sie ankommen und wo es hakt – direkt aus der Sendungsverfolgung von DHL.")}
        actions={
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t("Zeitraum")}>
            {([7, 30, 90] as const).map((x) => (
              <button key={x} role="radio" aria-checked={days === x} onClick={() => setDays(x)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", days === x ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{x} {t("Tage")}</button>
            ))}
          </div>
        }
      />
      <div className="px-4 md:px-8 space-y-4">
        {!d ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-24" />)}</div>
        ) : d.period.packages === 0 && Object.values(d.now).every((n) => n === 0) ? (
          <Card>
            <EmptyState icon={<PackageSearch className="size-5" />} title={t("Noch keine verfolgten Sendungen")}>
              {t("Sobald Logiful Pakete verschickt, fragen wir sie bei DHL ab – die ersten Zahlen erscheinen nach wenigen Stunden. Voraussetzung: Für eure Versandarten ist ein Versandkonto hinterlegt (")}<Link to="/settings/carrier-accounts" className="underline underline-offset-2">{t("Einstellungen → Versandkonten")}</Link>).
            </EmptyState>
          </Card>
        ) : (
          <>
            <section aria-label={t("Jetzt unterwegs")}>
              <h2 className="text-[13px] font-semibold mb-2 flex flex-wrap items-baseline gap-x-2">
                {t("Gerade unterwegs")}
                <span className="text-[12px] font-normal text-fg-muted">{t("· heute zugestellt:")} <span className="font-medium text-success tabular-nums">{fmtNumber(d.now.deliveredToday)}</span></span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-3">
                <Now icon={<PackageSearch className="size-4" />} label={t("Angekündigt")} value={d.now.pre_transit} sub={t("Noch nicht von DHL bearbeitet")} />
                <Now icon={<Truck className="size-4" />} label={t("Unterwegs")} value={d.now.in_transit} sub={t("im DHL-Netz auf dem Weg")} />
                <Now icon={<Clock className="size-4" />} label={t("In Zustellung")} value={d.now.out_for_delivery} sub={t("heute im Zustellfahrzeug")} />
                <Now icon={<Store className="size-4" />} label={t("Zur Abholung bereit")} value={d.now.pickup_ready} sub={t("liegt in Filiale oder Packstation")} tone={d.now.pickup_ready ? "warning" : undefined} />
                <Now icon={<AlertTriangle className="size-4" />} label={t("Zustellproblem")} value={d.now.exception} sub={t("nicht zugestellt – Grund am Auftrag")} tone={d.now.exception ? "danger" : undefined} />
                <Now icon={<PauseCircle className="size-4" />} label={t("Hängt fest")} value={d.now.stuck} sub={t("seit 3 Tagen kein neuer Scan")} tone={d.now.stuck ? "warning" : undefined} />
              </div>
            </section>

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <Kpi icon={<Timer className="size-4" />} label={t("Laufzeit Versand → Zustellung")} value={dur(d.period.medianH)} tip={t("Median: die Hälfte der Pakete ist schneller beim Empfänger. 90 % innerhalb des zweiten Werts. Gemessen ab der Versandmeldung des Lagers bis zum Zustell-Scan.")} sub={t("90 % in {v}", { v: dur(d.period.p90H) })} delta={d.period.medianH != null && d.prev.medianH != null ? (d.period.medianH - d.prev.medianH) / d.prev.medianH : null} unit="%" goodWhenDown />
              <Kpi icon={<CheckCircle2 className="size-4" />} label={t("Nach spätestens 2 Tagen da")} value={pct(d.period.delivered ? (d.period.buckets.d1 + d.period.buckets.d2) / d.period.delivered : null)} tip={t("Anteil der zugestellten Pakete, die innerhalb von 48 Stunden nach Versand beim Empfänger waren.")} sub={t("{v} schon am nächsten Tag", { v: fmtNumber(d.period.buckets.d1) })} />
              <Kpi icon={<PackageSearch className="size-4" />} label={t("Zugestellt")} value={fmtNumber(d.period.delivered)} sub={t("von {v} Paketen im Zeitraum", { v: fmtNumber(d.period.packages) })} delta={d.prev.delivered ? (d.period.delivered - d.prev.delivered) / d.prev.delivered : null} unit="%" />
              <Kpi
                icon={<RotateCcw className="size-4" />}
                label={t("Probleme & Rückläufer")}
                value={pct(d.period.packages ? (d.period.problems + d.period.returned) / d.period.packages : null)}
                tip={t("Pakete mit Zustellproblem oder Rücksendung an den Absender – häufig Adressfehler oder nicht angetroffene Empfänger.")}
                sub={t("{v} Problem · {v2} zurück", { v: fmtNumber(d.period.problems), v2: fmtNumber(d.period.returned) })}
                tone={d.period.packages && (d.period.problems + d.period.returned) / d.period.packages > 0.02 ? "warning" : undefined}
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <Card className="p-5 xl:col-span-2">
                <div className="flex flex-wrap items-baseline gap-3 mb-3">
                  <h2 className="font-semibold">{t("Zustellungen je Tag")}</h2>
                  <span className="text-[12px] text-fg-muted">{t("Balken: zugestellte Pakete · Farbe: Laufzeit (Median)")}</span>
                </div>
                <DailyChart daily={d.daily} />
              </Card>
              <Card className="p-5">
                <h2 className="font-semibold">{t("Wie lange unterwegs?")}</h2>
                <p className="text-[12px] text-fg-muted">{t("Zugestellte Pakete nach Laufzeit")}</p>
                <Buckets b={d.period.buckets} total={d.period.delivered} />
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card className="overflow-hidden">
                <div className="px-5 pt-4 pb-3"><h2 className="font-semibold flex items-center gap-2"><Truck className="size-4 text-fg-subtle" /> {t("Versandarten")}</h2></div>
                {d.methods.length ? (
                  <table className="w-full text-[13px]">
                    <thead className="text-[12px] text-fg-subtle border-y border-border bg-surface-2/50">
                      <tr><th className="h-8 pl-5 pr-3 text-left font-medium">{t("Versandart")}</th><th className="h-8 px-3 text-right font-medium">{t("Pakete")}</th><th className="h-8 px-3 text-right font-medium">{t("Laufzeit")}</th><th className="h-8 px-3 text-right font-medium">{t("≤ 2 Tage")}</th><th className="h-8 pl-3 pr-5 text-right font-medium">{t("Probleme")}</th></tr>
                    </thead>
                    <tbody>
                      {d.methods.map((m) => (
                        <tr key={m.method} className="border-b border-border last:border-0">
                          <td className="pl-5 pr-3 py-2"><span className="font-medium">{m.method}</span>{m.carrier && <span className="block text-[11.5px] text-fg-subtle">{m.carrier}</span>}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtNumber(m.packages)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{dur(m.medianH)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-fg-muted">{pct(m.within2)}</td>
                          <td className={cx("pl-3 pr-5 py-2 text-right tabular-nums", m.problems ? "text-danger" : "text-fg-muted")}>{pct(m.packages ? m.problems / m.packages : null)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="px-5 pb-5 text-[13px] text-fg-muted">{t("Im Zeitraum keine verfolgten Pakete.")}</p>}
              </Card>

              <Card className="overflow-hidden">
                <div className="px-5 pt-4 pb-3"><h2 className="font-semibold flex items-center gap-2"><Globe2 className="size-4 text-fg-subtle" /> {t("Länder")}</h2></div>
                {d.countries.length ? (
                  <table className="w-full text-[13px]">
                    <thead className="text-[12px] text-fg-subtle border-y border-border bg-surface-2/50">
                      <tr><th className="h-8 pl-5 pr-3 text-left font-medium">{t("Land")}</th><th className="h-8 px-3 text-right font-medium">{t("Pakete")}</th><th className="h-8 px-3 text-right font-medium">{t("Anteil")}</th><th className="h-8 pl-3 pr-5 text-right font-medium">{t("Laufzeit")}</th></tr>
                    </thead>
                    <tbody>
                      {d.countries.map((c) => (
                        <tr key={c.country} className="border-b border-border last:border-0">
                          <td className="pl-5 pr-3 py-2 font-medium">{c.country === "—" ? t("unbekannt") : countryName(c.country)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtNumber(c.packages)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-fg-muted">{pct(d.period.packages ? c.packages / d.period.packages : null)}</td>
                          <td className="pl-3 pr-5 py-2 text-right tabular-nums">{dur(c.medianH)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="px-5 pb-5 text-[13px] text-fg-muted">{t("Im Zeitraum keine verfolgten Pakete.")}</p>}
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {d.regions.length > 0 && (
                <Card className="p-5">
                  <h2 className="font-semibold flex items-center gap-2"><MapPinned className="size-4 text-fg-subtle" /> {t("Deutschland nach PLZ-Region")}</h2>
                  <p className="text-[12px] text-fg-muted">{t("Laufzeit (Median) je Leitzone – zeigt, wohin es länger dauert")}</p>
                  <Regions regions={d.regions} />
                </Card>
              )}
              <Card className={cx("overflow-hidden", !d.regions.length && "xl:col-span-2")}>
                <div className="px-5 pt-4 pb-3">
                  <h2 className="font-semibold flex items-center gap-2"><AlertTriangle className="size-4 text-fg-subtle" /> {t("Braucht Aufmerksamkeit")}</h2>
                  <p className="text-[12px] text-fg-muted">{t("Zustellprobleme, Rückläufer, lange liegende und hängende Pakete der letzten 30 Tage")}</p>
                </div>
                {d.attention.length ? (
                  <ul className="divide-y divide-border border-t border-border">
                    {d.attention.map((a) => (
                      <li key={`${a.orderId}-${a.trackingNumber}`}>
                        <Link to={`/orders?open=${a.orderId}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-5 py-2.5 text-[13px] hover:bg-surface-2/50">
                          <span className="font-mono font-medium">{a.orderNumber}</span>
                          <Badge tone={REASON[a.reason].tone}>{t(REASON[a.reason].label)}</Badge>
                          <span className="text-[12px] text-fg-subtle">{a.lastEventAt ? t("letzter Scan {v}", { v: fmtRelative(a.lastEventAt) }) : t("versendet {v}", { v: fmtRelative(a.shippedAt) })}</span>
                          {a.statusText && <span className="w-full truncate text-[12.5px] text-fg-muted">{a.statusText}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 pb-5 text-[13px] text-success flex items-center gap-1.5"><CheckCircle2 className="size-4" /> {t("Alles im Fluss – kein Paket braucht gerade Aufmerksamkeit.")}</p>
                )}
              </Card>
            </div>
            <p className="text-[12px] text-fg-subtle flex items-start gap-1.5"><Info className="size-3.5 mt-0.5 shrink-0" /> {t("Gezählt wird je Paket. Laufzeiten ab der Versandmeldung des Lagers bis zum Zustell-Scan von DHL, Wochenenden eingeschlossen.")}</p>
          </>
        )}
      </div>
    </div>
  );
}

function Now({ icon, label, value, sub, tone }: { icon: ReactNode; label: string; value: number; sub?: string; tone?: "success" | "warning" | "danger" }) {
  return (
    <Card className={cx("p-3.5", tone === "danger" && value > 0 && "border-danger/30 bg-danger-soft/30", tone === "warning" && value > 0 && "border-warning/30 bg-warning-soft/30")}>
      <span className="flex items-center gap-1.5 text-[12px] text-fg-muted"><span className="text-fg-subtle">{icon}</span>{t(label)}</span>
      <p className={cx("mt-1 text-[24px] font-semibold tabular-nums", tone === "danger" && value > 0 && "text-danger", tone === "success" && "text-success")}>{fmtNumber(value)}</p>
      {sub && <p className="text-[11.5px] text-fg-subtle leading-snug">{t(sub)}</p>}
    </Card>
  );
}

/** delta: Veränderung zum Vorzeitraum; goodWhenDown = weniger ist besser (z. B. Laufzeit) */
function Kpi({ icon, label, value, sub, tip, delta, unit, tone, goodWhenDown }: { icon: ReactNode; label: string; value: string; sub?: string; tip?: string; delta?: number | null; unit?: string; tone?: "warning"; goodWhenDown?: boolean }) {
  const head = <span className="flex items-center gap-2 text-fg-muted text-[12.5px]"><span className="text-fg-subtle">{icon}</span>{t(label)}{tip && <Info className="size-3 text-fg-subtle" />}</span>;
  return (
    <Card className="p-4">
      {tip ? <Tip content={tip}>{head}</Tip> : head}
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={cx("text-[26px] font-semibold tracking-tight tabular-nums whitespace-nowrap", tone === "warning" && "text-warning")}>{value}</span>
        {delta != null && Math.abs(delta) >= 0.001 && (
          <Badge tone={(delta < 0) === !!goodWhenDown ? "success" : "warning"} icon={delta < 0 ? <ArrowDownRight className="size-3" /> : <ArrowUpRight className="size-3" />}>
            {(Math.abs(delta) * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} {unit}
          </Badge>
        )}
      </div>
      {sub && <p className="text-[12px] text-fg-subtle">{t(sub)}</p>}
    </Card>
  );
}

/** Farbe nach Laufzeit: schnell grün, normal grau, langsam orange */
const speedTone = (h: number | null) => (h == null ? "bg-border-strong" : h <= 30 ? "bg-success" : h <= 48 ? "bg-ink/70" : "bg-warning");

function DailyChart({ daily }: { daily: TrackingOverview["daily"] }) {
  if (!daily.length) return <p className="text-[13px] text-fg-muted py-8 text-center">{t("Im Zeitraum noch keine Zustellungen.")}</p>;
  const max = Math.max(1, ...daily.map((d) => d.delivered));
  return (
    <div>
      <div className="h-40 flex items-end gap-[3px]">
        {daily.map((d) => (
          <Tip key={d.day} content={t("{v}: {delivered} zugestellt{v2}", { v: new Date(d.day).toLocaleDateString(intlLocale(), { weekday: "short", day: "2-digit", month: "2-digit" }), delivered: d.delivered, v2: d.medianH != null ? t(" · Laufzeit {v}", { v: dur(d.medianH) }) : "" })}>
            <div className="flex-1 min-w-0 h-full flex items-end">
              <div className={cx("w-full rounded-t-[2px]", speedTone(d.medianH))} style={{ height: `${Math.max(d.delivered ? 2 : 0, (d.delivered / max) * 100)}%` }} />
            </div>
          </Tip>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-fg-subtle">
        <span>{new Date(daily[0]!.day).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" })}</span>
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-success" />≤ 30 h</span>
          <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-ink/70" />≤ 48 h</span>
          <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-warning" />{t("länger")}</span>
        </span>
      </div>
    </div>
  );
}

function Buckets({ b, total }: { b: TrackingOverview["period"]["buckets"]; total: number }) {
  const rows = [
    { label: msg("nächster Tag (≤ 24 h)"), n: b.d1, cls: "bg-success" },
    { label: msg("2 Tage (≤ 48 h)"), n: b.d2, cls: "bg-success/60" },
    { label: msg("3 Tage (≤ 72 h)"), n: b.d3, cls: "bg-warning/70" },
    { label: msg("länger"), n: b.more, cls: "bg-warning" },
  ];
  return (
    <ul className="mt-4 space-y-3">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="flex justify-between text-[12.5px]"><span>{t(r.label)}</span><span className="tabular-nums text-fg-muted">{pct(total ? r.n / total : null)}</span></div>
          <div className="mt-1 h-2 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", r.cls)} style={{ width: `${total ? (r.n / total) * 100 : 0}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

function Regions({ regions }: { regions: TrackingOverview["regions"] }) {
  const max = Math.max(1, ...regions.map((r) => r.medianH ?? 0));
  return (
    <ul className="mt-3 space-y-1.5">
      {regions.map((r) => (
        <li key={r.zone} className="grid grid-cols-[1.5rem_1fr_4.5rem] items-center gap-2 text-[12.5px]">
          <span className="font-mono font-semibold text-fg-muted">{r.zone}</span>
          <div className="min-w-0">
            <div className="flex justify-between gap-2"><span className="truncate">{ZONES[r.zone] ?? "—"}</span><span className="text-[11.5px] text-fg-subtle tabular-nums">{fmtNumber(r.packages)}</span></div>
            <div className="mt-0.5 h-1.5 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", speedTone(r.medianH))} style={{ width: `${((r.medianH ?? 0) / max) * 100}%` }} /></div>
          </div>
          <span className="text-right tabular-nums">{dur(r.medianH)}</span>
        </li>
      ))}
    </ul>
  );
}
